import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { 
  ChartLine, 
  GraduationCap, 
  ShoppingBag, 
  TrendUp,
  TrendDown,
  Crown,
  ArrowRight,
  Clock
} from '@phosphor-icons/react';
import { Progress } from '../components/ui/progress';
import { Button } from '../components/ui/button';

const Dashboard = () => {
  const { user } = useAuth();
  const { fetchCart, cartCount } = useCart();

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const tierColors = {
    basic: 'text-[#C0C0C0]',
    premium: 'text-white',
    elite: 'text-[#D4AF37]'
  };

  const quickStats = [
    {
      label: 'Subscription',
      value: user?.subscription_tier?.charAt(0).toUpperCase() + user?.subscription_tier?.slice(1),
      icon: Crown,
      color: tierColors[user?.subscription_tier] || 'text-white'
    },
    {
      label: 'Cart Items',
      value: cartCount,
      icon: ShoppingBag,
      color: 'text-[#D4AF37]'
    }
  ];

  const recentSignals = [
    { symbol: 'BTC/USD', action: 'BUY', change: '+2.4%', trend: 'up' },
    { symbol: 'ETH/USD', action: 'BUY', change: '+1.8%', trend: 'up' },
    { symbol: 'TSLA', action: 'SELL', change: '-0.9%', trend: 'down' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] py-8" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-outfit text-3xl sm:text-4xl font-light text-white mb-2" data-testid="dashboard-welcome">
            Welcome back, <span className="text-[#D4AF37]">{user?.name}</span>
          </h1>
          <p className="text-zinc-400">Here's what's happening with your trading journey</p>
        </motion.div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {quickStats.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-[#121212] border border-white/5 p-6"
              data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
            >
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">{stat.label}</span>
                <stat.icon className={`w-5 h-5 ${stat.color}`} weight="duotone" />
              </div>
              <p className={`text-2xl font-outfit font-medium ${stat.color}`}>{stat.value}</p>
            </motion.div>
          ))}

          {/* Course Progress Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-[#121212] border border-white/5 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Course Progress</span>
              <GraduationCap className="w-5 h-5 text-[#D4AF37]" weight="duotone" />
            </div>
            <Progress value={35} className="h-2 bg-zinc-800" />
            <p className="text-sm text-zinc-400 mt-2">35% complete</p>
          </motion.div>

          {/* Trading Activity Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-[#121212] border border-white/5 p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">Active Signals</span>
              <ChartLine className="w-5 h-5 text-[#D4AF37]" weight="duotone" />
            </div>
            <p className="text-2xl font-outfit font-medium text-white">4</p>
          </motion.div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Signals */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="lg:col-span-2 bg-[#121212] border border-white/5 p-6"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-outfit text-xl text-white">Recent Signals</h2>
              <Link to="/trading" className="text-sm text-[#D4AF37] hover:underline flex items-center gap-1">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="space-y-4">
              {recentSignals.map((signal) => (
                <div 
                  key={signal.symbol}
                  className="flex items-center justify-between p-4 bg-black/30 border border-white/5"
                >
                  <div className="flex items-center gap-4">
                    {signal.trend === 'up' ? (
                      <TrendUp className="w-8 h-8 text-green-500" weight="bold" />
                    ) : (
                      <TrendDown className="w-8 h-8 text-red-500" weight="bold" />
                    )}
                    <div>
                      <p className="font-mono text-white font-medium">{signal.symbol}</p>
                      <p className={`text-sm ${signal.action === 'BUY' ? 'text-green-500' : 'text-red-500'}`}>
                        {signal.action}
                      </p>
                    </div>
                  </div>
                  <span className={`font-mono ${signal.trend === 'up' ? 'text-green-500' : 'text-red-500'}`}>
                    {signal.change}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-[#121212] border border-white/5 p-6"
          >
            <h2 className="font-outfit text-xl text-white mb-6">Quick Actions</h2>
            <div className="space-y-3">
              <Link to="/courses" className="block">
                <Button variant="outline" className="w-full justify-start border-zinc-800 text-zinc-300 hover:border-[#D4AF37] hover:text-[#D4AF37]">
                  <GraduationCap className="w-5 h-5 mr-3" weight="duotone" />
                  Continue Learning
                </Button>
              </Link>
              <Link to="/trading" className="block">
                <Button variant="outline" className="w-full justify-start border-zinc-800 text-zinc-300 hover:border-[#D4AF37] hover:text-[#D4AF37]">
                  <ChartLine className="w-5 h-5 mr-3" weight="duotone" />
                  View Charts
                </Button>
              </Link>
              <Link to="/shop" className="block">
                <Button variant="outline" className="w-full justify-start border-zinc-800 text-zinc-300 hover:border-[#D4AF37] hover:text-[#D4AF37]">
                  <ShoppingBag className="w-5 h-5 mr-3" weight="duotone" />
                  Browse Shop
                </Button>
              </Link>
              {user?.subscription_tier === 'basic' && (
                <Link to="/pricing" className="block">
                  <Button className="w-full bg-[#D4AF37] text-black hover:bg-[#E5C158]">
                    <Crown className="w-5 h-5 mr-3" weight="duotone" />
                    Upgrade Now
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>
        </div>

        {/* Subscription Banner for Basic Users */}
        {user?.subscription_tier === 'basic' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-8 bg-gradient-to-r from-[#D4AF37]/20 to-[#B38F24]/20 border border-[#D4AF37]/30 p-8"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="font-outfit text-2xl text-white mb-2">
                  Unlock Your Full Potential
                </h3>
                <p className="text-zinc-400">
                  Upgrade to Premium or Elite to access all signals, courses, and exclusive features.
                </p>
              </div>
              <Link to="/pricing">
                <Button className="bg-[#D4AF37] text-black hover:bg-[#E5C158] px-8 py-6 gold-glow">
                  View Plans
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
